import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { OwnerLayout } from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { Textarea } from '../../components/ui/textarea';
import { 
  ArrowLeft, ExternalLink, CheckCircle, Play, 
  FileText, Instagram, Youtube, Globe, Lock,
  ChevronLeft, ChevronRight, Undo2, Package, Maximize, Minimize, Tv
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

export default function OwnerVideoDetail() {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const { activeDog } = useAuth();
  const [video, setVideo] = useState(null);
  const [series, setSeries] = useState(null);
  const [allBoxVideos, setAllBoxVideos] = useState([]);
  const [progress, setProgress] = useState(null);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [markingComplete, setMarkingComplete] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const videoContainerRef = useRef(null);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  // Lock body scroll when in fullscreen mode
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isFullscreen]);

  // Allow Escape key to exit fullscreen
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape' && isFullscreen) setIsFullscreen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isFullscreen]);

  useEffect(() => {
    fetchData();
  }, [videoId, activeDog]);

  const fetchData = async () => {
    if (!activeDog) {
      setLoading(false);
      return;
    }

    try {
      const videoRes = await axios.get(`${API}/videos/${videoId}`);
      setVideo(videoRes.data);

      const [seriesRes, progressRes, allSeriesRes] = await Promise.all([
        axios.get(`${API}/series/${videoRes.data.series_id}`),
        axios.get(`${API}/progress/${activeDog.id}`),
        axios.get(`${API}/series`)
      ]);

      setSeries(seriesRes.data);

      // Build navigation across all boxes (training boxes only, not Welcome/Bonus)
      const boxVids = [];
      for (const s of allSeriesRes.data) {
        if (s.order_index <= 0) continue; // Skip Welcome & Bonus videos
        const vidsRes = await axios.get(`${API}/videos/series/${s.id}`);
        if (vidsRes.data.length > 0) {
          boxVids.push({ video: vidsRes.data[0], series: s });
        }
      }
      boxVids.sort((a, b) => a.series.order_index - b.series.order_index);
      setAllBoxVideos(boxVids);

      const videoProgress = progressRes.data.find(p => p.video_id === videoId);
      setProgress(videoProgress);
      setNotes(videoProgress?.owner_notes || '');
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load video');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkComplete = async () => {
    setMarkingComplete(true);
    try {
      await axios.post(`${API}/progress/mark-complete`, {
        dog_id: activeDog.id,
        video_id: videoId
      });
      toast.success('Box completed!', { description: 'Great job! Keep up the good work!' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark complete');
    } finally {
      setMarkingComplete(false);
    }
  };

  const handleMarkIncomplete = async () => {
    if (!window.confirm('This will also lock subsequent boxes. Continue?')) return;
    try {
      await axios.post(`${API}/progress/mark-incomplete`, {
        dog_id: activeDog.id,
        video_id: videoId
      });
      toast.success('Box marked as incomplete');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await axios.post(`${API}/progress/update-notes`, {
        dog_id: activeDog.id,
        video_id: videoId,
        notes
      });
      toast.success('Notes saved!');
    } catch (error) {
      toast.error('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const getVimeoEmbedUrl = (url) => {
    if (!url) return video?.embed_url || '';

    try {
      const parsed = new URL(url);
      let videoId = '';
      const originalParams = new URLSearchParams(parsed.search);

      if (parsed.hostname === 'player.vimeo.com') {
        const pathMatch = parsed.pathname.match(/\/video\/(\d+)/);
        videoId = pathMatch ? pathMatch[1] : '';
      } else if (parsed.hostname === 'vimeo.com' || parsed.hostname === 'www.vimeo.com') {
        const pathMatch = parsed.pathname.match(/\/(\d+)(?:\/([a-zA-Z0-9]+))?/);
        videoId = pathMatch ? pathMatch[1] : '';
        if (pathMatch && pathMatch[2]) {
          originalParams.set('h', pathMatch[2]);
        }
      }

      if (!videoId) return video?.embed_url || '';

      const embedParams = new URLSearchParams(originalParams);
      embedParams.set('fullscreen', '1');
      embedParams.set('pip', '1');
      embedParams.set('airplay', '1');
      embedParams.set('chromecast', '1');
      embedParams.set('quality', 'auto');
      embedParams.set('responsive', '1');
      embedParams.set('title', '0');
      embedParams.set('byline', '0');
      embedParams.set('portrait', '0');

      return `https://player.vimeo.com/video/${videoId}?${embedParams.toString()}`;
    } catch (e) {
      const match = url.match(/vimeo\.com\/(\d+)/);
      if (match) {
        return `https://player.vimeo.com/video/${match[1]}?fullscreen=1&pip=1&airplay=1&chromecast=1&quality=auto&responsive=1&title=0&byline=0&portrait=0`;
      }
      return video?.embed_url || '';
    }
  };

  // Box-to-box navigation
  const currentBoxIdx = allBoxVideos.findIndex(bv => bv.video.id === videoId);
  const prevBox = currentBoxIdx > 0 ? allBoxVideos[currentBoxIdx - 1] : null;
  const nextBox = currentBoxIdx < allBoxVideos.length - 1 ? allBoxVideos[currentBoxIdx + 1] : null;
  const totalBoxes = allBoxVideos.length;
  const currentBoxNum = currentBoxIdx >= 0 ? currentBoxIdx + 1 : 0;

  const isLocked = progress?.status === 'locked';
  const isComplete = progress?.status === 'complete';

  if (loading) {
    return (
      <OwnerLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-edupaws-sage border-t-transparent" />
        </div>
      </OwnerLayout>
    );
  }

  if (!activeDog) {
    return (
      <OwnerLayout>
        <div className="text-center py-16" data-testid="no-dog-prompt">
          <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <Play className="w-10 h-10 text-amber-400" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-gray-800 mb-4">
            Add a Dog to Get Started
          </h2>
          <p className="text-gray-500 font-body mb-6">
            You need to add your dog before you can start watching lessons.
          </p>
          <button
            onClick={() => navigate('/owner/dogs')}
            data-testid="go-to-dogs-btn"
            className="inline-block font-heading font-bold py-3 px-8 rounded-full text-white
              bg-edupaws-navy shadow-[0_4px_0_#3C4753] hover:shadow-[0_2px_0_#3C4753] hover:translate-y-[2px] transition-all"
          >
            Add a Dog
          </button>
        </div>
      </OwnerLayout>
    );
  }

  if (!video || isLocked) {
    return (
      <OwnerLayout>
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-gray-400" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-gray-800 mb-4">
            {isLocked ? 'Video Locked' : 'Video Not Found'}
          </h2>
          <p className="text-gray-500 font-body mb-6">
            {isLocked ? 'Complete the previous box to unlock this one' : 'This video does not exist'}
          </p>
          <button
            onClick={() => navigate('/owner/series')}
            className="inline-block font-heading font-bold py-3 px-8 rounded-full text-white
              bg-edupaws-navy shadow-[0_4px_0_#3C4753]"
          >
            Back to Training
          </button>
        </div>
      </OwnerLayout>
    );
  }

  return (
    <OwnerLayout>
      {/* Back Button */}
      <button
        onClick={() => navigate('/owner/series')}
        className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6 font-body"
        data-testid="back-to-series"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Training
      </button>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Video Player */}
          <div className="bg-white rounded-3xl overflow-hidden border-2 border-gray-100 shadow-lg">
            <div className="aspect-video bg-gray-900 relative">
              <iframe
                src={getVimeoEmbedUrl(video.watch_url)}
                className="w-full h-full"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                allowFullScreen
                webkitallowfullscreen="true"
                mozallowfullscreen="true"
                title={video.title}
                data-testid="video-player"
              />
              {/* Video controls overlay */}
              <div className="absolute bottom-3 right-3 z-10 flex items-center gap-2">
                {video.watch_url && (
                  <a
                    href={video.watch_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="cast-btn"
                    className="bg-black/60 hover:bg-black/80 text-white p-2 rounded-lg transition-colors backdrop-blur-sm"
                    title="Open on Vimeo (for AirPlay / Chromecast)"
                  >
                    <Tv className="w-5 h-5" />
                  </a>
                )}
                <button
                  onClick={toggleFullscreen}
                  data-testid="custom-fullscreen-btn"
                  className="bg-black/60 hover:bg-black/80 text-white p-2 rounded-lg transition-colors backdrop-blur-sm"
                  title="Fullscreen"
                >
                  <Maximize className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Fullscreen Overlay */}
          {isFullscreen && (
            <div
              ref={videoContainerRef}
              data-testid="fullscreen-overlay"
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100%',
                height: '100%',
                zIndex: 99999,
                background: '#000',
              }}
            >
              <iframe
                src={getVimeoEmbedUrl(video.watch_url)}
                style={{ width: '100%', height: '100%', border: 'none' }}
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                allowFullScreen
                webkitallowfullscreen="true"
                mozallowfullscreen="true"
                title={video.title}
              />
              <button
                onClick={toggleFullscreen}
                data-testid="exit-fullscreen-btn"
                style={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  zIndex: 100000,
                  background: 'rgba(0,0,0,0.7)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '10px 14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  backdropFilter: 'blur(8px)',
                }}
              >
                <Minimize className="w-5 h-5" />
                <span style={{ fontSize: 14, fontWeight: 600 }}>Close</span>
              </button>
            </div>
          )}

          {/* Video Info */}
          <div className="bg-white rounded-3xl p-6 border-2 border-gray-100">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                {currentBoxNum > 0 && (
                <span className="inline-block bg-edupaws-sage/20 text-edupaws-navy px-3 py-1 rounded-full text-sm font-heading font-bold mb-3">
                  Month {currentBoxNum} of {totalBoxes}
                </span>
                )}
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-800">
                  {series?.title || video.title}
                </h1>
                {series?.phase && (
                  <p className="text-sm text-gray-400 font-body mt-1">{series.phase}</p>
                )}
              </div>

              <span className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-body font-medium
                ${isComplete ? 'bg-edupaws-sage/20 text-edupaws-navy' : 'bg-amber-50 text-amber-700'}`}>
                {isComplete ? (
                  <><CheckCircle className="w-4 h-4" /> Completed</>
                ) : (
                  <><Play className="w-4 h-4" /> In Progress</>
                )}
              </span>
            </div>

            <div className="mb-6">
              <h3 className="font-heading font-bold text-gray-700 mb-2">Goal / Outcome</h3>
              <p className="text-gray-600 font-body">{video.goal_outcome}</p>
            </div>

            {/* Box Contents */}
            {(video.box_tool || video.box_toy || video.box_swag) && (
              <div className="mb-6 bg-gray-50 rounded-2xl p-5">
                <h3 className="font-heading font-bold text-edupaws-navy mb-3 flex items-center gap-2">
                  <Package className="w-5 h-5 text-edupaws-sage" />
                  What's in This Box
                </h3>
                <div className="grid sm:grid-cols-3 gap-3">
                  {video.box_tool && (
                    <div className="bg-white rounded-xl p-3">
                      <div className="text-xs text-gray-400 font-body mb-1">Tool</div>
                      <div className="text-sm font-body font-medium text-edupaws-navy">{video.box_tool}</div>
                    </div>
                  )}
                  {video.box_toy && (
                    <div className="bg-white rounded-xl p-3">
                      <div className="text-xs text-gray-400 font-body mb-1">Toy</div>
                      <div className="text-sm font-body font-medium text-edupaws-navy">{video.box_toy}</div>
                    </div>
                  )}
                  {video.box_swag && (
                    <div className="bg-white rounded-xl p-3">
                      <div className="text-xs text-gray-400 font-body mb-1">Swag</div>
                      <div className="text-sm font-body font-medium text-edupaws-navy">{video.box_swag}</div>
                    </div>
                  )}
                </div>
                {video.box_description && (
                  <p className="text-sm text-gray-500 font-body mt-3">{video.box_description}</p>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-3">
              {!isComplete ? (
                <button
                  onClick={handleMarkComplete}
                  disabled={markingComplete}
                  data-testid="mark-complete-btn"
                  className="flex items-center gap-2 font-heading font-bold py-3 px-6 rounded-full text-white
                    bg-edupaws-navy shadow-[0_4px_0_#3C4753] hover:shadow-[0_2px_0_#3C4753] hover:translate-y-[2px] transition-all
                    disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CheckCircle className="w-5 h-5" />
                  {markingComplete ? 'Marking...' : 'Mark as Complete'}
                </button>
              ) : (
                <button
                  onClick={handleMarkIncomplete}
                  data-testid="mark-incomplete-btn"
                  className="flex items-center gap-2 font-heading font-bold py-3 px-6 rounded-full text-gray-600
                    bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  <Undo2 className="w-5 h-5" />
                  Mark Incomplete
                </button>
              )}

              <a
                href={video.watch_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="watch-on-vimeo"
                className="flex items-center gap-2 font-heading font-bold py-3 px-6 rounded-full text-gray-600
                  bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <ExternalLink className="w-5 h-5" />
                Watch on Vimeo
              </a>
            </div>
          </div>

          {/* Box-to-Box Navigation */}
          <div className="flex items-center justify-between">
            {prevBox ? (
              <Link
                to={`/owner/video/${prevBox.video.id}`}
                data-testid="prev-box-link"
                className="flex items-center gap-2 text-gray-500 hover:text-edupaws-sage font-body transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
                <span className="hidden sm:block">{prevBox.series.title}</span>
                <span className="sm:hidden">Previous Box</span>
              </Link>
            ) : <div />}

            {nextBox ? (
              <Link
                to={`/owner/video/${nextBox.video.id}`}
                data-testid="next-box-link"
                className={`flex items-center gap-2 font-body transition-colors
                  ${isComplete ? 'text-edupaws-sage hover:text-edupaws-navy' : 'text-gray-300 cursor-not-allowed pointer-events-none'}`}
              >
                <span className="hidden sm:block">{nextBox.series.title}</span>
                <span className="sm:hidden">Next Box</span>
                <ChevronRight className="w-5 h-5" />
              </Link>
            ) : <div />}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Notes */}
          <div className="bg-white rounded-3xl p-6 border-2 border-gray-100">
            <h3 className="font-heading text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-edupaws-sage" />
              My Training Notes
            </h3>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Write notes about this training session..."
              data-testid="notes-textarea"
              className="min-h-[150px] mb-4 rounded-xl resize-none font-body"
            />
            <button
              onClick={handleSaveNotes}
              disabled={savingNotes}
              data-testid="save-notes-btn"
              className="w-full font-heading font-bold py-2.5 rounded-full text-white
                bg-edupaws-navy shadow-[0_3px_0_#3C4753] hover:shadow-[0_1px_0_#3C4753] hover:translate-y-[2px] transition-all
                disabled:opacity-50"
            >
              {savingNotes ? 'Saving...' : 'Save Notes'}
            </button>
          </div>

          {/* Resources */}
          {video.resource_links_json && video.resource_links_json.length > 0 && (
            <div className="bg-white rounded-3xl p-6 border-2 border-gray-100">
              <h3 className="font-heading text-lg font-bold text-gray-800 mb-4">Resources</h3>
              <div className="space-y-2">
                {video.resource_links_json.map((link, idx) => (
                  <a key={idx} href={link} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 text-edupaws-sage hover:text-edupaws-navy font-body text-sm">
                    <ExternalLink className="w-4 h-4" /> Resource {idx + 1}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Social Links */}
          {(video.tiktok_url || video.instagram_url || video.youtube_url || video.website_url) && (
            <div className="bg-white rounded-3xl p-6 border-2 border-gray-100">
              <h3 className="font-heading text-lg font-bold text-gray-800 mb-4">Follow Along</h3>
              <div className="flex gap-3">
                {video.tiktok_url && (
                  <a href={video.tiktok_url} target="_blank" rel="noopener noreferrer"
                    className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors">
                    <TikTokIcon className="w-5 h-5 text-gray-700" />
                  </a>
                )}
                {video.instagram_url && (
                  <a href={video.instagram_url} target="_blank" rel="noopener noreferrer"
                    className="w-10 h-10 bg-pink-100 hover:bg-pink-200 rounded-full flex items-center justify-center transition-colors">
                    <Instagram className="w-5 h-5 text-pink-600" />
                  </a>
                )}
                {video.youtube_url && (
                  <a href={video.youtube_url} target="_blank" rel="noopener noreferrer"
                    className="w-10 h-10 bg-red-100 hover:bg-red-200 rounded-full flex items-center justify-center transition-colors">
                    <Youtube className="w-5 h-5 text-red-600" />
                  </a>
                )}
                {video.website_url && (
                  <a href={video.website_url} target="_blank" rel="noopener noreferrer"
                    className="w-10 h-10 bg-blue-100 hover:bg-blue-200 rounded-full flex items-center justify-center transition-colors">
                    <Globe className="w-5 h-5 text-blue-600" />
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </OwnerLayout>
  );
}
